﻿import { FunctionComponent, useEffect, useMemo } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Select,
    Spacer,
    Text,
    VStack,
} from "@chakra-ui/react";
import { useUsers } from "../../api/users";
import { ApplicationRole, UserResponse, useUserAdSync } from "../../api";
import { IconDotsVertical, IconRefresh, IconSearch, IconUser, IconX } from "@tabler/icons-react";
import { useUserContext } from "../../context/UserContext";
import { SetPageTitle } from "../../components/SetPageTitle";
import { formatTimestampStr } from "@am-tax/fe-core";
import { RoleNames } from "../../hooks/useHasAccess";

const avatarColors = ["blue", "teal", "green", "purple", "orange", "pink", "cyan", "red"];
function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
}

const roleColorScheme: Record<ApplicationRole, string> = {
    [ApplicationRole.admin]: "red",
    [ApplicationRole.developer]: "purple",
    [ApplicationRole.contributor]: "blue",
    [ApplicationRole.reader]: "gray",
};

export const AdminUsersPage: FunctionComponent = () => {
    const appInsights = useAppInsightsContext();
    const navigate = useNavigate();
    const user = useUserContext();

    useEffect(() => {
        appInsights.trackPageView({ uri: "/admin/users" });
    }, [appInsights]);

    const { data: users = [] } = useUsers();
    const userAdSync = useUserAdSync();

    const [searchParams, setSearchParams] = useSearchParams();
    const search = searchParams.get("search") ?? "";
    const roleFilter = (searchParams.get("role") ?? "") as ApplicationRole | "";
    const registeredFilter = (searchParams.get("registered") ?? "") as "self" | "verified" | "";

    const setParam = (key: string, value: string) =>
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                value ? next.set(key, value) : next.delete(key);
                return next;
            },
            { replace: true }
        );

    const clearFilters = () =>
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                next.delete("search");
                next.delete("role");
                next.delete("registered");
                return next;
            },
            { replace: true }
        );

    const hasActiveFilters = !!(search || roleFilter || registeredFilter);

    const filtered = useMemo(
        () =>
            users.filter(u => {
                const matchesSearch =
                    !search || u.displayName.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
                const matchesRole = !roleFilter || u.appRoles.includes(roleFilter as ApplicationRole);
                const matchesRegistered = !registeredFilter || (registeredFilter === "self" ? u.isSelfRegistered : !u.isSelfRegistered);
                return matchesSearch && matchesRole && matchesRegistered;
            }),
        [users, search, roleFilter, registeredFilter]
    );

    return (
        <Box h="100%" overflow="auto" bg="gray.50">
            <SetPageTitle title="System Settings" />

            {/* Page Header */}
            <Flex align="center" px={6} py={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
                <HStack gap={2}>
                    <Text fontSize="xl" fontWeight="semibold">
                        Users
                    </Text>
                    <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                        {users.length}
                    </Badge>
                </HStack>
                <Spacer />
                <Button leftIcon={<Icon as={IconRefresh} />} size="sm" variant="outline" onClick={() => userAdSync.mutate()} isLoading={userAdSync.isLoading}>
                    AD Sync
                </Button>
            </Flex>

            <Box px={6} py={4}>
                {/* Toolbar */}
                <HStack mb={3} gap={3}>
                    <InputGroup maxW="360px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={IconSearch} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input placeholder="Find a user..." value={search} onChange={e => setParam("search", e.target.value)} bg="white" borderRadius="md" />
                    </InputGroup>
                    <Select size="sm" maxW="160px" bg="white" borderRadius="md" value={roleFilter} onChange={e => setParam("role", e.target.value)}>
                        <option value="">All roles</option>
                        {Object.values(ApplicationRole).map(r => (
                            <option key={r} value={r}>
                                {RoleNames[r]}
                            </option>
                        ))}
                    </Select>
                    <Select size="sm" maxW="180px" bg="white" borderRadius="md" value={registeredFilter} onChange={e => setParam("registered", e.target.value)}>
                        <option value="">All registrations</option>
                        <option value="self">Self-registered</option>
                        <option value="verified">AD-provisioned</option>
                    </Select>
                    {hasActiveFilters && (
                        <Button size="sm" variant="ghost" leftIcon={<Icon as={IconX} boxSize={3} />} onClick={clearFilters}>
                            Clear
                        </Button>
                    )}
                    <Spacer />
                    <Text fontSize="sm" color="gray.500">
                        {filtered.length} member{filtered.length !== 1 ? "s" : ""}
                    </Text>
                </HStack>

                {/* Column Headers */}
                <Flex
                    px={4}
                    py={2}
                    bg="gray.100"
                    borderTopRadius="md"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    border="1px solid"
                    borderBottomWidth={0}
                >
                    <Box flex="1">Member</Box>
                    <Box w="220px">Role(s)</Box>
                    <Box w="110px">Registered</Box>
                    <Box w="150px">Joined</Box>
                    <Box w="40px" />
                </Flex>

                {/* List */}
                <Box bg="white" border="1px solid" borderColor="gray.200" borderBottomRadius="md" overflow="hidden">
                    {filtered.length === 0 ? (
                        <VStack py={12} color="gray.400" gap={2}>
                            <Icon as={IconUser} boxSize={8} />
                            <Text fontSize="sm">No users found</Text>
                        </VStack>
                    ) : (
                        filtered.map((u, i) => (
                            <UserListRow
                                key={u.id}
                                user={u}
                                dateFormat={user.dateFormat}
                                onEdit={() => navigate(`/admin/users/${u.id}`)}
                                isLast={i === filtered.length - 1}
                            />
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    );
};

interface UserListRowProps {
    user: UserResponse;
    dateFormat: string;
    onEdit: () => void;
    isLast: boolean;
}

const UserListRow: FunctionComponent<UserListRowProps> = ({ user, dateFormat, onEdit, isLast }) => {
    const color = getAvatarColor(user.displayName);
    return (
        <Flex
            px={4}
            py={3}
            align="center"
            gap={3}
            borderBottom={isLast ? undefined : "1px solid"}
            borderColor="gray.100"
            _hover={{ bg: "gray.50" }}
            transition="background 0.15s"
        >
            <Avatar size="sm" name={user.displayName} bg={`${color}.400`} color="white" />
            <Box flex="1" minW={0}>
                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                    {user.displayName}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {user.email}
                </Text>
            </Box>
            <Flex w="220px" gap={1} flexWrap="wrap" align="center">
                {user.appRoles.length > 0 ? (
                    user.appRoles.map(r => (
                        <Badge key={r} colorScheme={roleColorScheme[r]} variant="subtle" fontSize="xs">
                            {RoleNames[r]}
                        </Badge>
                    ))
                ) : (
                    <Text fontSize="xs" color="gray.400">
                        —
                    </Text>
                )}
            </Flex>
            <Box w="110px">
                <Badge colorScheme={user.isSelfRegistered ? "gray" : "green"} variant="subtle" fontSize="xs">
                    {user.isSelfRegistered ? "Self" : "Verified"}
                </Badge>
            </Box>
            <Text w="150px" fontSize="xs" color="gray.500">
                {user.createdOn ? formatTimestampStr(user.createdOn, dateFormat) : "—"}
            </Text>
            <Menu>
                <MenuButton as={IconButton} icon={<Icon as={IconDotsVertical} boxSize={4} />} aria-label="User actions" size="xs" variant="ghost" w="40px" />
                <MenuList minW="120px">
                    <MenuItem onClick={onEdit}>Edit</MenuItem>
                </MenuList>
            </Menu>
        </Flex>
    );
};
